import { z } from 'zod';
import { AuditSchema, IdSchema } from './common.ts';

/**
 * The chart of accounts a builder costs a job against.
 *
 * Two levels, because that is what every estimate and every budget page in
 * the reference frames shows: a **category** with a numeric range
 * (`1000 - 1999 Preparation Preliminaries`) holding **codes** (`1010 Site
 * survey`). Phase 5's whole ledger hangs off this — estimate lines, purchase
 * orders, bills, receipts and invoices all carry a `costCodeId`, and a line
 * without one is a number nobody can roll up.
 *
 * **Referenced by id, never by number.** The number is what people say out
 * loud and what they reorganise every few years; the id is what the ledger
 * points at. So renumbering is a rename, and a five-year-old bill still
 * reports under the code it was filed against even after the whole chart is
 * restructured. `docs/DATA_MODEL.md` §6 says this and it is the one
 * decision here that would be expensive to get wrong.
 */

/**
 * What kind of cost a line is.
 *
 * Fixed rather than company-configurable, because these five are what
 * accounting integrations and job-costing reports expect, and a company
 * inventing a sixth would have it mean nothing to either. A code declares
 * which of them it accepts, so "Framing labour" cannot be billed as
 * material by accident.
 */
/**
 * The most markup anything in this product will accept: 1000%.
 *
 * **It was 100%**, on the stated grounds that more is likelier a typo
 * than an intention and a typo here reaches a customer as a price. That
 * reasoning was about a change-order line typed during a site visit,
 * and it is wrong for merchant materials: a connector bought at 0.42
 * and sold at 2.50 is a 495% markup, which is ordinary.
 *
 * **Raised rather than removed**, which matters. A ceiling catches
 * *every* writer — a CSV import, a callable, a future integration —
 * where a confirmation dialog catches only somebody looking at a
 * screen. Ten times cost sits far enough above real pricing that
 * reaching it means something is wrong: it refuses `4950` typed for
 * `495`, and nothing structural can refuse `495` typed for `49.5`,
 * which is why the markup fields still ask for confirmation above 100%.
 *
 * Lives here, and in one place, for the reason `MARKUP_UNITS_PER_PERCENT`
 * does: two call sites with their own literal is how a cap and its
 * documentation come to disagree. Everything that copies a markup from a
 * cost code shares this — the catalog item, the cost line and the
 * change-order line — because a code at 300% that a line could not
 * legally copy would be worse than no cap at all.
 *
 * **Tax rates are deliberately not this.** `rateMillionths` stays at
 * 100%, because a tax rate above 100% is not a pricing decision, it is
 * a mistake.
 */
export const MAX_MARKUP_MILLIONTHS = 10_000_000;

export const COST_TYPES = ['labor', 'material', 'equipment', 'sub', 'other'] as const;
export const CostTypeSchema = z.enum(COST_TYPES);
export type CostType = z.infer<typeof CostTypeSchema>;

export const COST_TYPE_LABEL: Record<CostType, string> = {
  labor: 'Labour',
  material: 'Material',
  equipment: 'Equipment',
  sub: 'Subcontractor',
  other: 'Other',
};

/**
 * A band of code numbers with a name.
 *
 * The range is stored as two integers rather than the string
 * `'1000-1999'` the frames display, so "is this code in this category"
 * and "do these two categories overlap" are comparisons rather than
 * parsing. `formatCostCodeRange` renders it back.
 */
export const CostCodeCategorySchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  name: z.string().trim().min(1).max(80),
  rangeStart: z.number().int().min(0).max(999_999),
  rangeEnd: z.number().int().min(0).max(999_999),
  /** Where it sits in the list. Categories are read in this order, not by range. */
  order: z.number().int().min(0),
  active: z.boolean().default(true),
}).refine((category) => category.rangeEnd >= category.rangeStart, {
  message: 'A category cannot end before it starts',
  path: ['rangeEnd'],
});
export type CostCodeCategory = z.infer<typeof CostCodeCategorySchema>;

export const CostCodeSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  /**
   * The number people say out loud: `1010`.
   *
   * A string rather than an integer, because a chart of accounts uses
   * `1010.1` and `02-150` as readily as `1010`, and because leading zeros
   * are part of how it reads. Uniqueness is per company and checked on
   * write; sorting uses `compareCostCodeNumbers`, which sorts `1010`
   * before `1100` and before `1010.1` the way a person expects.
   */
  number: z.string().trim().min(1).max(20),
  name: z.string().trim().min(1).max(120),
  categoryId: IdSchema,
  /**
   * The cost types this code accepts. Empty means all of them, which is
   * what an imported chart that says nothing about types means — refusing
   * every type would make such a code unusable rather than permissive.
   */
  costTypes: z.array(CostTypeSchema).default([]),
  /**
   * The markup suggested when a line picks this code, in the same units as
   * `CoLine.markupMillionths` — millionths, so 20% is
   * `200_000`. See the note on that field: the name is wrong by a hundred
   * and renaming it needs a migration (P5-00). Use `markupFromPercent`.
   */
  defaultMarkupMillionths: z.number().int().min(0).max(MAX_MARKUP_MILLIONTHS).default(0),
  /**
   * Inactive codes stay readable and stop being offered.
   *
   * Deleting a code that a bill from two years ago points at would turn
   * that bill into an orphan, so nothing deletes one — `active: false` is
   * what "we do not use this any more" means.
   */
  active: z.boolean().default(true),
  /** The id this code has in an accounting system, when one is connected. */
  externalId: z.string().trim().max(100).optional(),
});
export type CostCode = z.infer<typeof CostCodeSchema>;

/** `1000 - 1999`, as the frames show it. */
export function formatCostCodeRange(
  category: Pick<CostCodeCategory, 'rangeStart' | 'rangeEnd'>,
): string {
  return `${category.rangeStart} - ${category.rangeEnd}`;
}

/** Whether a code's number falls inside a category's band. */
export function numberIsInRange(
  number: string,
  category: Pick<CostCodeCategory, 'rangeStart' | 'rangeEnd'>,
): boolean {
  const leading = leadingNumber(number);
  if (leading === null) return false;
  return leading >= category.rangeStart && leading <= category.rangeEnd;
}

/**
 * The integer a code number starts with, or null if it starts with none.
 *
 * `'1010.1'` is 1010 and `'02-150'` is 2, which is what makes a range check
 * work on the formats a real chart of accounts uses. A code whose number is
 * entirely non-numeric belongs to no range, and that is a thing the UI
 * warns about rather than refuses: a company with a code called `SITE` is
 * not wrong, it is just outside this scheme.
 */
export function leadingNumber(number: string): number | null {
  const match = /^\s*(\d+)/.exec(number);
  if (!match) return null;
  return Number.parseInt(match[1]!, 10);
}

/**
 * Sort two code numbers the way somebody reading a chart expects.
 *
 * Numerically on the leading integer first, so `1100` follows `1010` rather
 * than preceding it as a string sort would (`'1010' < '1100'` happens to be
 * right, but `'900' > '1010'` is not). Then on the whole string, so
 * `1010.1` sits after `1010`. A non-numeric code sorts after every numeric
 * one, because that is where a reader looks for it.
 */
export function compareCostCodeNumbers(a: string, b: string): number {
  const leadingA = leadingNumber(a);
  const leadingB = leadingNumber(b);
  if (leadingA === null && leadingB === null) return a.localeCompare(b);
  if (leadingA === null) return 1;
  if (leadingB === null) return -1;
  if (leadingA !== leadingB) return leadingA - leadingB;
  return a.localeCompare(b, undefined, { numeric: true });
}

/**
 * Two code numbers are the same number once spacing and case are set aside.
 *
 * The same reasoning as `tagKey`: `'1010'` and `' 1010 '` are one code to
 * everybody but a database, and a chart of accounts holding both is a chart
 * nobody trusts. Case matters for `'02-A'` style codes, so it is folded
 * too.
 */
export function costCodeKey(number: string): string {
  return number.trim().toLowerCase().replace(/\s+/g, '');
}

/** Whether this code will accept a line of this type. */
export function acceptsCostType(code: Pick<CostCode, 'costTypes'>, type: CostType): boolean {
  // Empty means all. An imported chart that says nothing about types must
  // not end up refusing everything.
  return code.costTypes.length === 0 || code.costTypes.includes(type);
}

/** Codes by id, for rendering a line's `costCodeId` as `1010 Site survey`. */
export function costCodesByIdFrom(codes: readonly CostCode[]): ReadonlyMap<string, CostCode> {
  return new Map(codes.map((code) => [code.id, code] as const));
}

/** `1010 Site survey`, which is how a code appears on a line. */
export function formatCostCode(code: Pick<CostCode, 'number' | 'name'>): string {
  return `${code.number} ${code.name}`;
}

export interface CostCodeGroup {
  readonly category: CostCodeCategory;
  readonly codes: readonly CostCode[];
}

/**
 * Codes grouped under their categories, in display order.
 *
 * Grouped by `categoryId` rather than by number range, deliberately. The
 * range is what a person uses to decide where a *new* code belongs; where
 * an *existing* code is filed is whatever its `categoryId` says, even when
 * somebody has renumbered it out of its category's band. Grouping by range
 * would silently move a code out of the category its owner put it in, and
 * the owner would have no way to put it back.
 *
 * A code whose category has been removed is returned in `orphans` rather
 * than dropped, because a code nobody can see is a code nobody can refile.
 */
export function groupCostCodes(
  categories: readonly CostCodeCategory[],
  codes: readonly CostCode[],
): { readonly groups: readonly CostCodeGroup[]; readonly orphans: readonly CostCode[] } {
  const byCategory = new Map<string, CostCode[]>();
  const known = new Set(categories.map((category) => category.id));
  const orphans: CostCode[] = [];

  for (const code of codes) {
    if (!known.has(code.categoryId)) {
      orphans.push(code);
      continue;
    }
    const bucket = byCategory.get(code.categoryId);
    if (bucket) bucket.push(code);
    else byCategory.set(code.categoryId, [code]);
  }

  for (const bucket of byCategory.values()) {
    bucket.sort((a, b) => compareCostCodeNumbers(a.number, b.number));
  }
  orphans.sort((a, b) => compareCostCodeNumbers(a.number, b.number));

  const groups = [...categories]
    .sort((a, b) => a.order - b.order || a.rangeStart - b.rangeStart)
    .map((category) => ({ category, codes: byCategory.get(category.id) ?? [] }));

  return { groups, orphans };
}

/**
 * Which category a new code's number suggests.
 *
 * Only a suggestion, and only for a code being created: the form offers it
 * and a person may pick another. Returns null when the number falls in no
 * range or in more than one, because guessing between two overlapping
 * categories is worse than asking.
 */
export function suggestedCategoryFor(
  number: string,
  categories: readonly CostCodeCategory[],
): CostCodeCategory | null {
  const matches = categories.filter(
    (category) => category.active && numberIsInRange(number, category),
  );
  return matches.length === 1 ? matches[0]! : null;
}

/**
 * Pairs of categories whose ranges overlap.
 *
 * Overlapping ranges are **allowed** — a company mid-reorganisation has
 * them, and refusing the write would strand them halfway. But they make
 * `suggestedCategoryFor` give up, so the settings page says which pairs are
 * responsible rather than leaving somebody wondering why the suggestion
 * stopped working.
 */
export function overlappingCategories(
  categories: readonly CostCodeCategory[],
): readonly (readonly [CostCodeCategory, CostCodeCategory])[] {
  const active = categories.filter((category) => category.active);
  const pairs: (readonly [CostCodeCategory, CostCodeCategory])[] = [];
  for (let i = 0; i < active.length; i += 1) {
    for (let j = i + 1; j < active.length; j += 1) {
      const a = active[i]!;
      const b = active[j]!;
      if (a.rangeStart <= b.rangeEnd && b.rangeStart <= a.rangeEnd) pairs.push([a, b]);
    }
  }
  return pairs;
}
